import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Play, RotateCcw, ShieldCheck, UserRound, XCircle } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, ToneChip } from "../components/common";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const PERSONA_TONES = { executive: "ai", pm: "info", legal: "warning", finance: "success", external: "muted" };

export default function DemoCenter() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirmResetAll, setConfirmResetAll] = useState(false);
  const [switching, setSwitching] = useState(null);

  const { data } = useQuery({ queryKey: ["demo-mode"], queryFn: async () => (await api.get("/demo-mode/escenarios")).data });
  const { data: ready, refetch: refetchReady, isFetching: checking } = useQuery({
    queryKey: ["demo-ready"], queryFn: async () => (await api.get("/demo-mode/ready")).data, retry: false,
  });

  const resetMut = useMutation({
    mutationFn: async (sid) => (await api.post(`/demo-mode/escenarios/${sid}/reset`)).data,
    onSuccess: () => { toast.success("Escenario restaurado a su estado inicial"); qc.invalidateQueries(); refetchReady(); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });
  const resetAllMut = useMutation({
    mutationFn: async () => (await api.post("/demo-mode/reset-all", { confirm: true })).data,
    onSuccess: () => { toast.success("Demo Release restaurada a la baseline certificada"); setConfirmResetAll(false); setTimeout(() => window.location.reload(), 800); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });

  const startStory = (s) => {
    localStorage.setItem("ts_demo_guide", JSON.stringify({ story: s.id, step: 0, open: true }));
    window.dispatchEvent(new Event("ts-demo-guide"));
    navigate(s.start_route);
  };

  const switchPersona = async (key, email) => {
    setSwitching(key);
    try {
      const u = await login(email, "Demo123");
      toast.success(`Viewing as: ${u.name}`);
      navigate("/demo-center");
    } catch (e) {
      toast.error(apiError(e, "No se pudo cambiar de persona"));
    } finally {
      setSwitching(null);
    }
  };

  const personaActual = Object.entries(data?.personas || {}).find(([, p]) => p.email === user?.email);

  return (
    <div className="space-y-6" data-testid="demo-center">
      <PageHeader title="Demo Center"
        subtitle="Recorridos guiados y reiniciables sobre la baseline certificada. Cada historia muestra: información → control → decisión → acción → evidencia → resultado."
        testid="demo-title"
        actions={<>
          <Button variant="outline" size="sm" onClick={() => refetchReady()} disabled={checking} data-testid="demo-ready-btn">
            <ShieldCheck className="mr-1 h-4 w-4" /> {checking ? "Comprobando…" : "Demo Ready Check"}
          </Button>
          {(user?.roles || []).includes("DEMO_ADMIN") && (
            <Button variant="outline" size="sm" className="text-red-700" onClick={() => setConfirmResetAll(true)} data-testid="demo-reset-all-btn">
              <RotateCcw className="mr-1 h-4 w-4" /> Reset Demo Release
            </Button>
          )}
        </>} />

      <div className="ts-card flex flex-wrap items-center gap-2 p-3" data-testid="demo-personas">
        <span className="ts-caption mr-1 flex items-center gap-1"><UserRound className="h-3.5 w-3.5" /> Viewing as:</span>
        <ToneChip label={`${user?.name} (${personaActual?.[1]?.label || "sin persona"})`} tone="info" testid="demo-viewing-as" />
        <span className="mx-1 text-slate-300">|</span>
        <span className="ts-caption mr-1">Cambiar persona (login real):</span>
        {Object.entries(data?.personas || {}).map(([key, p]) => (
          <Button key={key} size="sm" variant="outline" className="h-7 text-xs" disabled={switching === key || p.email === user?.email}
            onClick={() => switchPersona(key, p.email)} data-testid={`demo-persona-${key}`}>
            {switching === key ? "Entrando…" : p.label}
          </Button>
        ))}
      </div>

      {ready && (
        <div className={`ts-card p-4 ${ready.status === "DEMO READY" ? "border-l-4 border-l-emerald-600" : "border-l-4 border-l-red-600"}`} data-testid="demo-ready-panel">
          <p className={`font-display text-base font-bold ${ready.status === "DEMO READY" ? "text-emerald-700" : "text-red-700"}`} data-testid="demo-ready-status">{ready.status}</p>
          <ul className="mt-2 grid gap-1 sm:grid-cols-2">
            {ready.checks.map((c, i) => (
              <li key={i} className="flex items-center gap-1.5 text-xs" data-testid={`ready-check-${i}`}>
                {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-red-600" />}
                <span className={c.ok ? "text-slate-600" : "font-medium text-red-700"}>{c.check}{c.detail ? ` — ${c.detail}` : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="demo-scenarios">
        {(data?.scenarios || []).map((s) => (
          <div key={s.id} className="ts-card ts-card-hover flex flex-col p-5" data-testid={`demo-card-${s.id}`}>
            <div className="flex items-center gap-2">
              <h2 className="font-display text-base font-semibold text-slate-900">{s.name}</h2>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1">
              <ToneChip label={data.personas[s.persona]?.label || s.persona} tone={PERSONA_TONES[s.persona] || "info"} />
              <ToneChip label={s.duration} tone="muted" />
            </div>
            <p className="mt-2 text-sm text-slate-600">{s.objective}</p>
            <p className="mt-1 text-xs text-slate-400">Demuestra: {s.demuestra}</p>
            {s.state?.last_reset_at && <p className="mt-1 text-[10px] text-slate-400">Último reset: {s.state.last_reset_at.slice(0, 16).replace("T", " ")}</p>}
            <div className="mt-3 flex gap-2 border-t border-slate-100 pt-3">
              <Button size="sm" className="h-7 text-xs" onClick={() => startStory(s)} data-testid={`demo-start-${s.id}`}><Play className="mr-1 h-3 w-3" /> Start Demo</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => resetMut.mutate(s.id)} disabled={resetMut.isPending} data-testid={`demo-reset-${s.id}`}><RotateCcw className="mr-1 h-3 w-3" /> Reset</Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={confirmResetAll} onOpenChange={setConfirmResetAll}>
        <DialogContent data-testid="demo-reset-all-dialog">
          <DialogHeader><DialogTitle>Reset Demo Release</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Restaura la baseline certificada completa (TECH_STONE_BASELINE_PROMPT_06_CERTIFIED). Los datos generados durante las demos se eliminan. Requiere DEMO_ADMIN.</p>
          <div className="flex gap-2">
            <Button variant="destructive" onClick={() => resetAllMut.mutate()} disabled={resetAllMut.isPending} data-testid="demo-reset-all-confirm">
              {resetAllMut.isPending ? "Restaurando…" : "Confirmar reset completo"}
            </Button>
            <Button variant="ghost" onClick={() => setConfirmResetAll(false)}>Cancelar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
